import React from 'react';
import { Mail, Phone, MapPin, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import LogoHeader from '../../assets/logo-header.svg';
import { handleEmailClick } from '../../utils/emailHandler';
import FooterWalker from './FooterWalker';

export default function Footer() {
  return (
    <footer
      className="relative border-t border-white/5 bg-[#050508] mt-16 sm:mt-20 md:mt-24 pt-14 md:pt-20 pb-8 md:pb-10 z-40 overflow-x-clip"
      style={{ overflowX: 'clip' }}
    >

      {/* Running Animated RGB bottom neon line */}
      <div className="absolute top-0 left-0 w-full h-[1.5px] bg-gradient-to-r from-cyber-blue via-cyber-purple to-cyber-pink shadow-[0_0_8px_rgba(0,240,255,0.4)]"></div>

      {/* Animated Game Character Walking along Top Footer Path */}
      <FooterWalker />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        <div className="grid grid-cols-2 md600:grid-cols-12 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-12 pb-8 md:pb-16">

          {/* Column 1: Brand Logo & Mission */}
          <div className="col-span-2 md600:col-span-12 lg:col-span-3 space-y-5 text-center sm:text-left md:text-center lg:text-left">
            <div className="flex items-center justify-center sm:justify-start md:justify-center lg:justify-start">
              <div className="h-14 sm:h-16 flex items-center justify-center sm:justify-start">
                <img
                  src={LogoHeader}
                  alt="CUBE GAMES"
                  className="h-full w-auto object-contain filter"
                />
              </div>
            </div>

            <div className="space-y-2.5 max-w-sm mx-auto sm:mx-0 md:mx-auto lg:mx-0">
              <h4 className="text-cyber-blue font-cyber font-semibold text-base sm:text-lg tracking-tight">Level Up Your Library</h4>
              <p className="text-gray-400 text-xs sm:text-sm leading-relaxed font-sans font-light">
                Buy and download games instantly from our online gaming store.
              </p>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="col-span-1 md600:col-span-4 lg:col-span-2 space-y-5 lg:text-left">
            <h4 className="font-cyber font-semibold text-xs sm:text-sm uppercase tracking-wider text-white border-b border-white/10 pb-2 inline-block whitespace-nowrap">
              Quick Links
            </h4>

            <ul className="space-y-2.5 text-xs sm:text-sm text-gray-400 font-sans font-light">
              <li>
                <Link
                  to="/"
                  className="group flex items-center justify-start hover:text-cyber-purple transition-colors duration-200 py-0.5"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-cyber-purple mr-1.5 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200 shrink-0" />
                  <span>Home</span>
                </Link>
              </li>

              <li>
                <Link
                  to="/all-games"
                  className="group flex items-center justify-start hover:text-cyber-purple transition-colors duration-200 py-0.5"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-cyber-purple mr-1.5 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200 shrink-0" />
                  <span>Games</span>
                </Link>
              </li>

              <li>
                <Link
                  to="/platforms"
                  className="group flex items-center justify-start hover:text-cyber-purple transition-colors duration-200 py-0.5"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-cyber-purple mr-1.5 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200 shrink-0" />
                  <span>Platforms</span>
                </Link>
              </li>

              <li>
                <Link
                  to="/contact"
                  className="group flex items-center justify-start hover:text-cyber-purple transition-colors duration-200 py-0.5"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-cyber-purple mr-1.5 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200 shrink-0" />
                  <span>Contact</span>
                </Link>
              </li>

              <li>
                <Link
                  to="/blog"
                  className="group flex items-center justify-start hover:text-cyber-purple transition-colors duration-200 py-0.5"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-cyber-purple mr-1.5 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200 shrink-0" />
                  <span>Blogs</span>
                </Link>
              </li>
            </ul>
          </div>


          {/* Column 3: Support */}
          <div className="col-span-1 md600:col-span-4 lg:col-span-2 space-y-5 lg:text-left">
            <h4 className="font-cyber font-semibold text-xs sm:text-sm uppercase tracking-wider text-white border-b border-white/10 pb-2 inline-block whitespace-nowrap">
              Support
            </h4>

            <ul className="space-y-2.5 text-xs sm:text-sm text-gray-400 font-sans font-light">
              <li>
                <Link
                  to="/faq"
                  className="group flex items-center justify-start hover:text-cyber-blue transition-colors duration-200 py-0.5"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-cyber-blue mr-1.5 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200 shrink-0" />
                  <span>FAQs</span>
                </Link>
              </li>

              <li>
                <Link
                  to="/privacy"
                  className="group flex items-center justify-start hover:text-cyber-blue transition-colors duration-200 py-0.5"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-cyber-blue mr-1.5 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200 shrink-0" />
                  <span>Privacy Policy</span>
                </Link>
              </li>

              <li>
                <Link
                  to="/terms"
                  className="group flex items-center justify-start hover:text-cyber-blue transition-colors duration-200 py-0.5"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-cyber-blue mr-1.5 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200 shrink-0" />
                  <span>Terms of Service</span>
                </Link>
              </li>
            </ul>
          </div>


          {/* Column 4: Contact Info */}
          <div className="col-span-2 md600:col-span-4 lg:col-span-5 space-y-5 lg:text-left">
            <h4 className="font-cyber font-semibold text-xs sm:text-sm uppercase tracking-wider text-white border-b border-white/10 pb-2 inline-block whitespace-nowrap">
              Contact Info
            </h4>

            <div className="space-y-4">

              {/* Email */}
              <div className="flex items-start justify-start gap-3.5 group max-[425px]:flex-col max-[425px]:items-start max-[425px]:gap-2.5">
                <div className="p-2 sm:p-2.5 rounded-lg bg-white/5 border border-white/10 group-hover:border-cyber-purple group-hover:shadow-[0_0_12px_rgba(168,85,247,0.25)] transition-all shrink-0 mt-0.5 max-[425px]:mt-0">
                  <Mail className="w-4 h-4 text-cyber-purple" />
                </div>

                <div className="min-w-0 text-left">
                  <p className="text-[10px] text-gray-400 font-cyber font-medium uppercase tracking-widest mb-0.5">
                    Email us
                  </p>

                  <a
                    href="mailto:info@cubegames.ca"
                    onClick={handleEmailClick}
                    className="text-xs sm:text-sm text-gray-300 hover:text-white transition-colors font-sans break-all sm:break-normal block"
                  >
                    info@cubegames.ca
                  </a>
                </div>
              </div>


              {/* Phone */}
              <div className="flex items-start justify-start gap-3.5 group max-[425px]:flex-col max-[425px]:items-start max-[425px]:gap-2.5">
                <div className="p-2 sm:p-2.5 rounded-lg bg-white/5 border border-white/10 group-hover:border-cyber-blue group-hover:shadow-[0_0_12px_rgba(0,240,255,0.25)] transition-all shrink-0 mt-0.5 max-[425px]:mt-0">
                  <Phone className="w-4 h-4 text-cyber-blue" />
                </div>

                <div className="min-w-0 text-left">
                  <p className="text-[10px] text-gray-400 font-cyber font-medium uppercase tracking-widest mb-0.5">
                    Call us
                  </p>

                  <a
                    href="tel:1800-9797-5422"
                    className="text-xs sm:text-sm text-gray-300 hover:text-white transition-colors font-sans block"
                  >
                    1800-9797-5422
                  </a>

                  <a
                    href="tel:1800-9797-0000"
                    className="text-xs sm:text-sm text-gray-300 hover:text-white transition-colors font-sans block"
                  >
                    1800-9797-0000
                  </a>
                </div>
              </div>


              {/* Location */}
              <div className="flex items-start justify-start gap-3.5 group max-[425px]:flex-col max-[425px]:items-start max-[425px]:gap-2.5">
                <div className="p-2 sm:p-2.5 rounded-lg bg-white/5 border border-white/10 group-hover:border-cyber-pink group-hover:shadow-[0_0_12px_rgba(255,0,122,0.25)] transition-all shrink-0 mt-0.5 max-[425px]:mt-0">
                  <MapPin className="w-4 h-4 text-cyber-pink" />
                </div>

                <div className="min-w-0 text-left">
                  <p className="text-[10px] text-gray-400 font-cyber font-medium uppercase tracking-widest mb-0.5">
                    Location
                  </p>

                  <a
                    href="https://www.google.com/maps/search/?api=1&query=124 Alison Ave Cambridge, ON N1R 1N4 Canada"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gray-300 hover:text-gray-200 font-sans leading-relaxed block"
                  >
                    124 Alison Ave Cambridge, ON N1R 1N4 Canada
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Bottom copyright alignment */}
        <div className="pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-center gap-3 text-[10px] sm:text-xs text-gray-500 font-mono text-center sm:text-left">
          <p>© 2026 CUBE GAMES. All rights reserved by YOYO GAMES INC.</p>

        </div>
      </div>
    </footer>
  );
}

